import { useEffect, useRef, useState, type FC } from 'react'
import { observer } from 'mobx-react-lite'
import { Button, Icon, Typography } from 'shared/ui'
import { isLink, logger } from 'shared/lib'
import { makeTextFieldAutoFocus, TextField } from 'shared/ui/TextField'
import { type ShortLinkStore } from 'entities/ShortLink/store/shortLinkStore'
import styles from './styles.module.scss'

type IShortLinkContentProps = {
  store: ShortLinkStore
}

export const ShortLinkContent: FC<IShortLinkContentProps> = observer(({ store }) => {
  const [link, setLink] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)

  const { editLink, createShortLink, handleShortenLink, handleAddShortLink, handleSaveLink } = store

  const ref = useRef<HTMLTextAreaElement>(null)
  const isAddDisabled = !link.trim() || loading || !store.canShortUrl(link)

  const onChangeValue = (value: string) => {
    setError(false)
    setLink(value)
  }

  useEffect(() => {
    if (ref.current) {
      const end = editLink?.href.length || 0
      ref.current.setSelectionRange(end, end)
      ref.current.focus()
    }
  }, [editLink])

  useEffect(() => {
    if (editLink) {
      setLink(editLink.href)
    }
  }, [editLink])

  const onAdd = async () => {
    if (!isLink(link)) {
      setError(true)
      return
    }
    setError(false)
    try {
      const shortLink = await createShortLink(link)
      if (shortLink) {
        if (editLink) {
          handleShortenLink(shortLink)
        } else {
          handleAddShortLink(shortLink)
        }
      }
    } catch (e) {
      logger.error(e)
    } finally {
      setLoading(false)
    }
  }

  const onSave = () => {
    if (!isLink(link)) {
      setError(true)
      return
    }
    setError(false)
    handleSaveLink(link)
  }

  return (
    <div className={styles.wrap}>
      <TextField
        className={styles.textField}
        variant='integrated'
        size='small'
        InputProps={{ placeholder: 'Enter your URL' }}
        onChange={onChangeValue}
        value={link}
        ariaLabel='LabelShortenUrl'
        mainActions={[makeTextFieldAutoFocus({ withFocus: true })]}
      />
      {error && (
        <div className={styles.helper}>
          <Icon
            icon={'alertTriangleSmall'}
            color={'var(--content-negative-primary)'}
            fontSize={16}
          />
          <Typography
            variant={'text-input-helper'}
            color={'negativePrimary'}
            ariaLabel={'Please enter'}
          >
            Please enter a valid URL
          </Typography>
        </div>
      )}
      <div className={styles.actions}>
        {editLink ? (
          <>
            <Button
              text={'Shorten URL'}
              size={'medium'}
              contained={'secondary'}
              disabled={isAddDisabled}
              onClick={onAdd}
            />
            <Button
              text={'Save'}
              size={'medium'}
              contained={'primary'}
              disabled={!link.trim()}
              onClick={onSave}
            />
          </>
        ) : (
          <Button
            text={'Add URL'}
            size={'medium'}
            contained={'secondary'}
            disabled={isAddDisabled}
            onClick={onAdd}
          />
        )}
      </div>
    </div>
  )
})
